import { useState, useEffect } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import ScrollableWithIndicators from '../../../components/ScrollableWithIndicators';

const TabWrap = styled(Flex) <{
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  isMinimized?: boolean;
}>`
  background: ${({ isSelected }) => (isSelected ? '#E7F0FF' : 'rgba(255, 255, 255, 0.3)')};
  transition: all 0.3s ease;
  flex-direction: column;
  height: ${({ isMinimized }) => (isMinimized ? '62px' : '82px')};
  width: 136px;
  justify-content: center;
  align-items: center;
  font-size: ${({ isMinimized }) => (isMinimized ? '12px' : 'inherit')};
  cursor: pointer;
  color: #344F7A;
  box-shadow: ${({ isSelected }) => (isSelected ? '20px 2px 10px 6px rgba(0, 0, 0, 0.15)' : 'none')};
  user-select:none ;

  &:hover {
    background: ${({ isSelected }) => (isSelected ? '' : '#5f7497')};
    color: ${({ isSelected }) => (isSelected ? '' : 'white')};
  }
`;

const ContentWrapper = styled(Flex)`
  /* border: 3px solid rgb(95, 116, 151); */
  z-index: 10;
  box-shadow: 15px 15px 10px rgba(0, 0, 0, 0.15);
  position: absolute;
  left: 148px;
  width: 50vw;
  background-color: #E7F0FF;
  height: calc(100vh - 146px);
  max-height: calc(100vh - 146px);
  overflow: auto;
  border-radius: 0 20px 20px 0;
  padding: 8px;

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }

  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

const CloseButtonWrapper = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${({ theme }) => theme.color.primary || '#333'};
    stroke-width: 2;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
  }

  &:focus {
    outline: none;
  }
`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const CloseButton = ({ onClick, style = {} }: { onClick: () => void; style?: any }) => (
  <CloseButtonWrapper onClick={onClick} aria-label="Close" style={{ ...style }}>
    <CloseIcon />
  </CloseButtonWrapper>
);

const Tab = ({
  title,
  icon,
  isSelected,
  onClick,
  isFirst,
  isLast,
  isMinimized,
}: {
  title: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  isFirst: boolean;
  isLast: boolean;
  isMinimized?: boolean;
}) => (
  <TabWrap
    isMinimized={isMinimized}
    isFirst={isFirst}
    isLast={isLast}
    isSelected={isSelected}
    onClick={onClick}
    role="tab"
    aria-selected={isSelected}
  >
    {icon}
    <Text mt={1} fontSize={'14px'} >
      {title}
    </Text>
  </TabWrap>
);

export interface ITab {
  title: string;
  name: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  contentWrapperStyle?: React.CSSProperties;
  hide?: boolean
}

interface ITabulatorProps {
  tabs: ITab[];
  onTabChanged?: (e: string | undefined) => void;
  selectedTab?: string;
  isMinimized?: boolean;
}

export const TabulatorHpi: React.FC<ITabulatorProps> = ({
  tabs,
  onTabChanged,
  selectedTab,
  isMinimized,
}) => {
  const [currentTab, setCurrentTab] = useState<string | undefined>(selectedTab);

  useEffect(() => {
    onTabChanged?.(currentTab);
  }, [currentTab, onTabChanged]);

  const handleTabClick = (name: string) => {
    setCurrentTab((prev) => (prev !== name ? name : undefined));
  };

  const activeTab = tabs.find((tab) => tab.name === currentTab);

  return (
    <Flex width="100%" height="100%" flexDirection="column" sx={{ overflow: 'hidden' }}>
      <ScrollableWithIndicators>
        <Flex
          flexDirection={isMinimized ? 'row' : 'column'}
          px={isMinimized ? '20px' : 0}
          role="tablist"
        >
          {tabs.filter(x => !x.hide).map((tab, index) => (
            <Tab
              key={tab.name}
              {...tab}
              isSelected={tab.name === currentTab}
              isFirst={index === 0}
              isLast={index === tabs.length - 1}
              isMinimized={isMinimized}
              onClick={() => handleTabClick(tab.name)}
            />
          ))}
        </Flex>
      </ScrollableWithIndicators>

      {activeTab && (
        <ContentWrapper style={activeTab.contentWrapperStyle}>
          {activeTab.content}
          <CloseButton onClick={() => setCurrentTab(undefined)} />
        </ContentWrapper>
      )}
    </Flex>
  );
};
